/**
 * Represent a sequence of possibly repeating ids by at most three that keep
 * order and variety: the three most frequent ids, in first-seen order, with
 * runs trimmed until three remain. The timeline's collapsed-group dots.
 */
export function representByThree(ids: number[]): number[] {
  const counts = new Map<number, number>();
  for (const id of ids) counts.set(id, (counts.get(id) ?? 0) + 1);
  const top3 = [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([id]) => id);

  // runs of equal ids, top-3 only
  const groups: number[][] = [];
  for (const id of ids.filter((id) => top3.includes(id))) {
    const last = groups[groups.length - 1];
    if (last && last[0] === id) last.push(id);
    else groups.push([id]);
  }

  let total = groups.reduce((n, g) => n + g.length, 0);
  while (total > 3) {
    for (const id of [...top3].reverse()) {
      const gs = groups.filter((g) => g[0] === id);
      if (gs.length === 1) {
        if (gs[0].length > 1) {
          gs[0].pop();
          if (--total === 3) break;
        }
        continue;
      }
      const max = Math.max(...gs.map((g) => g.length));
      const victim = [...gs].reverse().find((g) => g.length === max);
      victim?.shift();
      if (--total === 3) break;
    }
  }
  return groups.filter((g) => g.length).flat();
}
